import { useExamCenter } from '@exam-center/exam-center-details';
import {
  ExamLocationRequestWorkflowStepCode,
  FilterOperator,
  OrderType,
  useSearchExamRoomRequestInfosQuery,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  type DataGridProps,
  DescriptionOutlinedIcon,
  type GridEventListener,
  type GridRowParams,
  NoRowsOverlay,
} from '@shared/components';
import dayjs from 'dayjs';

import {
  BaseExamLocationRequestList,
  rejectedColumns,
  type RejectedExamLocationRequest,
} from '../../components';
import { useExamLocationRequestListSearchParams } from '../../hooks';

export default function RejectedExamLocationRequestList() {
  const navigate = useNavigate();

  const { examCenter } = useExamCenter();

  const [
    { province, locationName, page, pageSize, sortBy, sortOrder },
    { changePageNumber, changePageSize, changeSorting },
  ] = useExamLocationRequestListSearchParams();

  const handleSortModelChange: DataGridProps['onSortModelChange'] = (sortModel) => {
    const sort = sortModel[0]?.sort ?? 'desc';
    changeSorting({ sortOrder: sort, sortBy: 'requestedAt' });
  };

  const { data, loading } = useSearchExamRoomRequestInfosQuery({
    variables: {
      searchExamRoomRequestInfosInput: {
        provinceId: province,
        searchLocationName: locationName,
        order: {
          field: sortBy ?? 'requestedAt',
          type: sortOrder === 'asc' ? OrderType.Asc : OrderType.Desc,
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        filter: {
          conditions: [
            {
              field: 'examCenterCode',
              value: examCenter.code,
              operator: FilterOperator.Equal,
            },
            {
              field: 'step',
              value: [ExamLocationRequestWorkflowStepCode.ExamLocationRequestRejected],
              operator: FilterOperator.In,
            },
          ],
        },
      },
    },
  });

  const examLocationRequests = data?.ecSearchExamRoomRequestInfos.examRoomRequestInfos ?? [];
  const totalCount = data?.ecSearchExamRoomRequestInfos.total ?? 0;

  const examLocationRequestItems: RejectedExamLocationRequest[] = examLocationRequests.map(
    (item) => ({
      id: item.id,
      refCode: item.taskCode ?? '-',
      buildingName:
        item.examLocationRequestInfo.buildingName ??
        item.examLocationRequestInfo.building?.nameTh ??
        '-',
      locationName: item.examLocationRequestInfo.nameTh ?? '-',
      floor: item.floor,
      roomName: item.name,
      roomNumber: item.roomNumber ?? '-',
      province: item.examLocationRequestInfo.province.nameTh,
      status: 'rejected',
      rejectedAt:
        item.examLocationRequestVerdicts.length > 0
          ? item.examLocationRequestVerdicts.reduce((max, curr) =>
              dayjs(curr.submittedAt).isAfter(dayjs(max.submittedAt)) ? curr : max,
            ).submittedAt
          : null,
    }),
  );

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<RejectedExamLocationRequest>,
  ) => {
    navigate(`/:examCenterCode/exam-location/requests/:roomRequestId`, {
      params: {
        roomRequestId: params.row.id,
        examCenterCode: examCenter.code,
      },
    });
  };

  const isSearchOrFiltering = (locationName?.length ?? 0) > 0 || Boolean(province);

  return (
    <BaseExamLocationRequestList
      columns={rejectedColumns}
      loading={loading}
      rows={examLocationRequestItems}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => {
          if (isSearchOrFiltering) {
            return <NoRowsOverlay />;
          }
          return (
            <NoRowsOverlay
              title='ยังไม่มีคำขออนุมัติสถานที่สอบที่ไม่อนุมัติ'
              icon={DescriptionOutlinedIcon}
            />
          );
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
      onSortModelChange={handleSortModelChange}
    />
  );
}
